/**
 * MUI TextField v4-to-v9 Compatibility Wrapper
 *
 * MUI v9 removed legacy sub-component props from TextField:
 *   - `InputProps`       → `slotProps.input`
 *   - `inputProps`       → `slotProps.htmlInput`
 *   - `InputLabelProps`  → `slotProps.inputLabel`
 *   - `SelectProps`      → `slotProps.select`
 *   - `FormHelperTextProps` → `slotProps.formHelperText`
 *
 * This wrapper translates the v4 API so that existing code
 * keeps working without per-file rewrites.
 */
import React from 'react';
import { TextField as MuiTextField } from '@mui/material';

function mergeSlotProps(existing, additions) {
  if (!additions || Object.keys(additions).length === 0) return existing;
  if (!existing) return additions;

  const merged = { ...existing };
  for (const [key, val] of Object.entries(additions)) {
    if (val === undefined) continue;
    const prev = merged[key];
    if (
      prev &&
      typeof prev === 'object' &&
      typeof val === 'object' &&
      !React.isValidElement(val) &&
      !Array.isArray(val)
    ) {
      // Deep-merge one level (handles classes, style, etc.)
      merged[key] = { ...prev, ...val };
    } else {
      merged[key] = val;
    }
  }
  return merged;
}

const TextFieldCompat = React.forwardRef(function TextFieldCompat(props, ref) {
  const {
    // Legacy props removed in v9
    InputProps,
    inputProps,
    InputLabelProps,
    SelectProps,
    FormHelperTextProps,
    // Pass-through
    slotProps: slotPropsProp,
    ...rest
  } = props;

  // Build slotProps from legacy props
  let slotProps = slotPropsProp || {};

  if (InputProps) {
    slotProps = { ...slotProps, input: mergeSlotProps(slotProps.input, InputProps) };
  }
  if (inputProps) {
    slotProps = { ...slotProps, htmlInput: mergeSlotProps(slotProps.htmlInput, inputProps) };
  }
  if (InputLabelProps) {
    slotProps = { ...slotProps, inputLabel: mergeSlotProps(slotProps.inputLabel, InputLabelProps) };
  }
  if (SelectProps) {
    slotProps = { ...slotProps, select: mergeSlotProps(slotProps.select, SelectProps) };
  }
  if (FormHelperTextProps) {
    slotProps = {
      ...slotProps,
      formHelperText: mergeSlotProps(slotProps.formHelperText, FormHelperTextProps),
    };
  }

  const hasSlotProps = Object.keys(slotProps).length > 0;

  return <MuiTextField ref={ref} {...rest} {...(hasSlotProps ? { slotProps } : {})} />;
});

export default TextFieldCompat;
